// libraryFilenameGenerator.ts - Gerador de nomes para PDFs da Biblioteca
// Bigwa - Janeiro 2026
// Padrão: Autor_Ano_Titulo.pdf ou Autor_Ano.pdf

import { Author, Work } from '../types/work';

// ═══════════════════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════════════════

export interface LibraryNamingConfig {
  // Configurações gerais
  separator: '_' | '-' | '.';
  maxTitleWords: number;        // Máximo de palavras do título (0 = não incluir)
  maxTotalLength: number;       // Comprimento máximo do nome (sem extensão)
  includeTitle: boolean;        // Se inclui título no nome
  
  // Formato do autor
  authorFormat: 'lastName' | 'lastNameInitial' | 'fullName';
  
  // Para duplicados
  suffixStyle: 'letter' | 'number';  // Santos_2020a ou Santos_2020_1
}

export interface GenerateFilenameInput {
  authors: Author[];
  year?: number;
  title?: string;
  existingNames?: string[];     // Nomes já existentes (para evitar duplicados)
  originalExtension?: string;   // Extensão original do arquivo
}

export interface GenerateFilenameResult {
  filename: string;             // Nome completo com extensão
  baseName: string;             // Nome sem extensão
  suffix?: string;              // Sufixo usado (a, b, c ou 1, 2, 3)
  wasTruncated: boolean;        // Se título foi truncado
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURAÇÃO PADRÃO
// ═══════════════════════════════════════════════════════════════════════════════

export const DEFAULT_LIBRARY_NAMING_CONFIG: LibraryNamingConfig = {
  separator: '_',
  maxTitleWords: 4,
  maxTotalLength: 80,
  includeTitle: true,
  authorFormat: 'lastName',
  suffixStyle: 'letter',
};

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Normaliza string para uso em nome de arquivo
 * Remove acentos, caracteres especiais, múltiplos espaços
 */
export function normalizeForFilename(text: string): string {
  if (!text) return '';
  
  return text
    // Normaliza unicode (NFD separa acentos)
    .normalize('NFD')
    // Remove acentos
    .replace(/[\u0300-\u036f]/g, '')
    // Remove caracteres especiais (mantém letras, números, espaços, hífen)
    .replace(/[^a-zA-Z0-9\s\-]/g, '')
    // Remove múltiplos espaços
    .replace(/\s+/g, ' ')
    // Trim
    .trim();
}

/**
 * Capitaliza primeira letra de cada palavra
 */
export function capitalizeWords(text: string): string {
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formata autor para nome de arquivo
 */
export function formatAuthorForFilename(
  authors: Author[],
  format: LibraryNamingConfig['authorFormat']
): string {
  if (!authors || authors.length === 0) {
    return 'Desconhecido';
  }
  
  const firstAuthor = authors[0];
  
  if (!firstAuthor.lastName && !firstAuthor.firstName) {
    return 'Desconhecido';
  }
  
  const lastName = normalizeForFilename(firstAuthor.lastName || firstAuthor.firstName || '');
  
  switch (format) {
    case 'lastName':
      // Santos
      return capitalizeWords(lastName);
      
    case 'lastNameInitial':
      // Santos-J
      const initial = firstAuthor.firstName?.charAt(0)?.toUpperCase() || '';
      return `${capitalizeWords(lastName)}${initial ? '-' + initial : ''}`;
      
    case 'fullName':
      // Santos-Joao
      const firstName = normalizeForFilename(firstAuthor.firstName || '');
      return `${capitalizeWords(lastName)}${firstName ? '-' + capitalizeWords(firstName) : ''}`;
      
    default:
      return capitalizeWords(lastName);
  }
}

/**
 * Formata título para nome de arquivo
 * Remove palavras comuns, limita tamanho
 */
export function formatTitleForFilename(
  title: string,
  maxWords: number,
  separator: string
): string {
  if (!title || maxWords === 0) return '';
  
  // Palavras a ignorar (artigos, preposições comuns)
  const stopWords = new Set([
    'a', 'o', 'as', 'os', 'um', 'uma', 'uns', 'umas',
    'de', 'da', 'do', 'das', 'dos', 'em', 'na', 'no', 'nas', 'nos',
    'para', 'por', 'com', 'sem', 'sob', 'sobre',
    'e', 'ou', 'que', 'se',
    'the', 'a', 'an', 'of', 'in', 'on', 'for', 'to', 'and', 'or', 'with',
  ]);
  
  const normalized = normalizeForFilename(title);
  
  // Divide em palavras e filtra
  const words = normalized
    .split(' ')
    .filter(word => word.length > 1 && !stopWords.has(word.toLowerCase()));
  
  // Pega as primeiras N palavras significativas
  const selectedWords = words.slice(0, maxWords);
  
  // Capitaliza e junta
  return selectedWords
    .map(word => capitalizeWords(word))
    .join(separator);
}

/**
 * Gera sufixo para duplicados
 */
export function generateSuffix(
  index: number,
  style: LibraryNamingConfig['suffixStyle']
): string {
  if (index === 0) return '';
  
  if (style === 'letter') {
    // a, b, c, ... z, aa, ab, ...
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    if (index <= 26) {
      return letters[index - 1];
    }
    // Para mais de 26, usa aa, ab, etc.
    const first = Math.floor((index - 1) / 26);
    const second = (index - 1) % 26;
    return letters[first - 1] + letters[second];
  } else {
    // 1, 2, 3, ...
    return index.toString();
  }
}

/**
 * Verifica se um nome já existe na lista (case-insensitive)
 */
export function nameExists(name: string, existingNames: string[]): boolean {
  const normalizedName = name.toLowerCase();
  return existingNames.some(existing => existing.toLowerCase() === normalizedName);
}

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÃO PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Gera nome de arquivo para PDF da Biblioteca
 * 
 * Padrões gerados:
 * - Santos_2020_Educacao_Digital.pdf (normal)
 * - Santos_2020.pdf (sem título ou título muito curto)
 * - Santos_2020a.pdf (duplicado)
 * - Santos_2020b_Outro_Artigo.pdf (duplicado com título diferente)
 */
export function generateLibraryFilename(
  input: GenerateFilenameInput,
  config: LibraryNamingConfig = DEFAULT_LIBRARY_NAMING_CONFIG
): GenerateFilenameResult {
  const { authors, year, title, existingNames = [], originalExtension = '.pdf' } = input;
  const { separator, maxTitleWords, maxTotalLength, includeTitle, authorFormat, suffixStyle } = config;
  
  // 1. Formatar autor
  const authorPart = formatAuthorForFilename(authors, authorFormat);
  
  // 2. Formatar ano
  const yearPart = year?.toString() || 'nd';
  
  // 3. Formatar título (se configurado)
  let titlePart = '';
  let wasTruncated = false;
  
  if (includeTitle && title && maxTitleWords > 0) {
    titlePart = formatTitleForFilename(title, maxTitleWords, separator);
    
    // Verifica se foi truncado (original tinha mais palavras)
    const originalWords = normalizeForFilename(title).split(' ').filter(w => w.length > 1).length;
    wasTruncated = originalWords > maxTitleWords;
  }
  
  // 4. Montar nome base (sem sufixo e extensão)
  let baseName = titlePart
    ? `${authorPart}${separator}${yearPart}${separator}${titlePart}`
    : `${authorPart}${separator}${yearPart}`;
  
  // 5. Truncar se necessário
  if (baseName.length > maxTotalLength) {
    baseName = baseName.substring(0, maxTotalLength);
    wasTruncated = true;
    
    // Remove separador no final se ficou cortado
    while (baseName.endsWith(separator) || baseName.endsWith('-')) {
      baseName = baseName.slice(0, -1);
    }
  }
  
  // 6. Verificar duplicados e adicionar sufixo se necessário
  let suffix = '';
  let finalBaseName = baseName;
  let index = 0;
  
  // Garantir extensão com ponto
  const extension = originalExtension.startsWith('.') 
    ? originalExtension.toLowerCase() 
    : '.' + originalExtension.toLowerCase();
  
  while (nameExists(finalBaseName + extension, existingNames)) {
    index++;
    suffix = generateSuffix(index, suffixStyle);
    finalBaseName = `${baseName}${suffix}`;
  }
  
  // 7. Montar resultado final
  const filename = finalBaseName + extension;
  
  return {
    filename,
    baseName: finalBaseName,
    suffix: suffix || undefined,
    wasTruncated,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE CONVENIÊNCIA
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Gera nome a partir de um Work completo
 * 
 * @param work - Dados do trabalho
 * @param existingNames - Nomes existentes para evitar duplicatas
 * @param config - Configuração de nomeação (padrão ou das preferências do usuário)
 * @param options - Opções incrementais (prefixo de projeto, tipo de trabalho)
 */
export function generateFilenameFromWork(
  work: Partial<Work>,
  existingNames: string[] = [],
  config: LibraryNamingConfig = DEFAULT_LIBRARY_NAMING_CONFIG,
  options?: {
    projectPrefix?: string;    // Nome do projeto para prefixar
    includeWorkType?: boolean; // Adicionar tipo ao final
  }
): GenerateFilenameResult {
  const result = generateLibraryFilename({
    authors: work.authors || [],
    year: work.year,
    title: work.title,
    existingNames,
    originalExtension: '.pdf',
  }, config);

  // ═══ INCREMENTAIS: Prefixo de projeto e tipo de trabalho ═══
  if (options?.projectPrefix || options?.includeWorkType) {
    const sep = config.separator;
    let baseName = result.baseName;
    
    // Prefixo de projeto: MeuProjeto_Santos_2020_Titulo.pdf
    if (options.projectPrefix) {
      const normalizedPrefix = normalizeForFilename(options.projectPrefix)
        .split(' ')
        .map(w => capitalizeWords(w))
        .join('');
      if (normalizedPrefix) {
        baseName = `${normalizedPrefix}${sep}${baseName}`;
      }
    }
    
    // Tipo de trabalho: Santos_2020_Titulo_artigo.pdf
    if (options.includeWorkType && work.type) {
      const typeLabels: Record<string, string> = {
        'article': 'artigo',
        'book': 'livro',
        'chapter': 'capitulo',
        'thesis': 'tese',
        'conference': 'conferencia',
        'report': 'relatorio',
        'website': 'web',
      };
      const typeLabel = typeLabels[work.type] || work.type;
      baseName = `${baseName}${sep}${typeLabel}`;
    }
    
    const extension = '.pdf';
    
    // Re-verificar duplicados com o nome estendido
    let finalBaseName = baseName;
    let suffix = '';
    let index = 0;
    
    while (nameExists(finalBaseName + extension, existingNames)) {
      index++;
      suffix = generateSuffix(index, config.suffixStyle);
      finalBaseName = `${baseName}${suffix}`;
    }
    
    return {
      filename: finalBaseName + extension,
      baseName: finalBaseName,
      suffix: suffix || undefined,
      wasTruncated: result.wasTruncated,
    };
  }

  return result;
}

/**
 * Verifica se um nome de arquivo segue o padrão da biblioteca
 * Retorna info extraída ou null se não segue o padrão
 */
export function parseLibraryFilename(filename: string): {
  author: string;
  year: string;
  title?: string;
  suffix?: string;
} | null {
  // Remove extensão
  const baseName = filename.replace(/\.[^/.]+$/, '');
  
  // Padrões possíveis:
  // Autor_Ano
  // Autor_Ano_Titulo
  // Autor_Anoa (com sufixo)
  // Autor_Anoa_Titulo
  
  // Tenta match com separadores comuns
  const patterns = [
    /^([A-Za-z\-]+)[_\-.](\d{4})([a-z])?(?:[_\-.](.+))?$/,
  ];
  
  for (const pattern of patterns) {
    const match = baseName.match(pattern);
    if (match) {
      return {
        author: match[1],
        year: match[2],
        suffix: match[3] || undefined,
        title: match[4] || undefined,
      };
    }
  }
  
  return null;
}

/**
 * Sugere renomeação para arquivo existente
 */
export function suggestRename(
  currentFilename: string,
  work: Partial<Work>,
  existingNames: string[] = [],
  config: LibraryNamingConfig = DEFAULT_LIBRARY_NAMING_CONFIG
): {
  shouldRename: boolean;
  currentName: string;
  suggestedName: string;
  reason?: string;
} {
  const result = generateFilenameFromWork(work, existingNames, config);
  
  // Normaliza para comparação
  const current = currentFilename.toLowerCase();
  const suggested = result.filename.toLowerCase();
  
  const shouldRename = current !== suggested;
  
  let reason: string | undefined;
  if (shouldRename) {
    const parsed = parseLibraryFilename(currentFilename);
    if (!parsed) {
      reason = 'Nome atual não segue o padrão da biblioteca';
    } else if (parsed.author.toLowerCase() !== formatAuthorForFilename(work.authors || [], config.authorFormat).toLowerCase()) {
      reason = 'Nome do autor diferente';
    } else if (parsed.year !== work.year?.toString()) {
      reason = 'Ano diferente';
    } else {
      reason = 'Padronização do nome';
    }
  }
  
  return {
    shouldRename,
    currentName: currentFilename,
    suggestedName: result.filename,
    reason,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTEGRAÇÃO COM PREFERENCES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Converte NamingPreferences do preferencesStore para LibraryNamingConfig
 * Usado para aplicar as preferências do usuário na geração de nomes
 */
export function namingPreferencesToConfig(prefs: {
  separator?: '_' | '-' | '.';
  authorFormat?: 'lastName' | 'lastNameInitial' | 'fullName';
  includeTitle?: boolean;
  maxTitleWords?: number;
}): LibraryNamingConfig {
  return {
    ...DEFAULT_LIBRARY_NAMING_CONFIG,
    separator: prefs.separator || DEFAULT_LIBRARY_NAMING_CONFIG.separator,
    authorFormat: prefs.authorFormat || DEFAULT_LIBRARY_NAMING_CONFIG.authorFormat,
    includeTitle: prefs.includeTitle ?? DEFAULT_LIBRARY_NAMING_CONFIG.includeTitle,
    maxTitleWords: prefs.maxTitleWords ?? DEFAULT_LIBRARY_NAMING_CONFIG.maxTitleWords,
  };
}

/**
 * Gera nome usando preferências do usuário (lê do preferencesStore)
 * Função de conveniência que unifica config + opções incrementais
 */
export function generateFilenameWithPreferences(
  work: Partial<Work>,
  existingNames: string[] = [],
  namingPrefs: {
    separator?: '_' | '-' | '.';
    authorFormat?: 'lastName' | 'lastNameInitial' | 'fullName';
    includeTitle?: boolean;
    maxTitleWords?: number;
    includeProjectPrefix?: boolean;
    includeWorkType?: boolean;
  },
  projectName?: string
): GenerateFilenameResult {
  const config = namingPreferencesToConfig(namingPrefs);
  const options = {
    projectPrefix: namingPrefs.includeProjectPrefix ? projectName : undefined,
    includeWorkType: namingPrefs.includeWorkType || false,
  };
  
  return generateFilenameFromWork(work, existingNames, config, options);
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTS DEFAULT
// ═══════════════════════════════════════════════════════════════════════════════

export default {
  generateLibraryFilename,
  generateFilenameFromWork,
  generateFilenameWithPreferences,
  namingPreferencesToConfig,
  parseLibraryFilename,
  suggestRename,
  normalizeForFilename,
  formatAuthorForFilename,
  formatTitleForFilename,
  DEFAULT_LIBRARY_NAMING_CONFIG,
};
